<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import FormsAPI from 'custom/dashboard/api/forms';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import CrmSettingsHeader from '../crm/CrmSettingsHeader.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const forms = ref([]);
const isLoading = ref(true);

const fetchForms = async () => {
  try {
    const { data } = await FormsAPI.get();
    forms.value = data;
  } catch {
    useAlert(t('CARDS.FORMS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const editForm = form =>
  router.push({
    name: 'forms_edit',
    params: { ...route.params, formId: form.id },
  });

const copyLink = async form => {
  await copyTextToClipboard(form.public_url);
  useAlert(t('CARDS.FORMS.LINK_COPIED'));
};

// On/off, confirmed either way: it decides whether the link customers already have takes
// submissions.
const toggleDialog = ref(null);
const pendingToggle = ref(null);
const isToggling = ref(false);

const pendingStatus = computed(() =>
  pendingToggle.value?.status === 'active' ? 'disabled' : 'active'
);
const toggleTexts = computed(() => {
  const name = pendingToggle.value?.name;
  return pendingStatus.value === 'active'
    ? {
        title: t('CARDS.FORMS.TURN_ON.TITLE', { name }),
        description: t('CARDS.FORMS.TURN_ON.DESCRIPTION'),
        confirm: t('CARDS.FORMS.TURN_ON.CONFIRM'),
        done: t('CARDS.FORMS.TURNED_ON'),
      }
    : {
        title: t('CARDS.FORMS.TURN_OFF.TITLE', { name }),
        description: t('CARDS.FORMS.TURN_OFF.DESCRIPTION'),
        confirm: t('CARDS.FORMS.TURN_OFF.CONFIRM'),
        done: t('CARDS.FORMS.TURNED_OFF'),
      };
});

const askToggle = form => {
  pendingToggle.value = form;
  toggleDialog.value.open();
};

const confirmToggle = async () => {
  const form = pendingToggle.value;
  const { done } = toggleTexts.value;
  isToggling.value = true;
  try {
    const { data } = await FormsAPI.update(form.id, {
      form: { status: pendingStatus.value },
    });
    form.status = data.status;
    useAlert(done);
    toggleDialog.value.close();
  } catch {
    useAlert(t('CARDS.FORMS.TOGGLE_ERROR'));
  } finally {
    isToggling.value = false;
  }
};

onMounted(fetchForms);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('CARDS.FORMS.LOADING')"
    :no-records-found="!forms.length"
    :no-records-message="$t('CARDS.FORMS.EMPTY')"
  >
    <template #header>
      <CrmSettingsHeader />
    </template>

    <template #body>
      <ul
        class="flex flex-col mb-0 list-none border divide-y ltr:pl-0 rtl:pr-0 rounded-xl border-n-weak divide-n-weak"
      >
        <li
          v-for="form in forms"
          :key="form.id"
          class="flex items-center gap-4 px-4 py-3"
          :data-form-id="form.id"
        >
          <div class="flex flex-col flex-1 min-w-0 gap-1">
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ form.name }}
              </span>
              <span
                v-if="form.system_key"
                class="px-1.5 text-xs rounded bg-n-alpha-2 text-n-slate-11"
                data-testid="form-system"
              >
                {{ $t('CARDS.FORMS.SYSTEM') }}
              </span>
              <span
                v-if="form.status !== 'active'"
                class="px-1.5 text-xs rounded bg-n-ruby-3 text-n-ruby-11"
                data-testid="form-disabled"
              >
                {{ $t('CARDS.FORMS.DISABLED') }}
              </span>
            </div>
            <span class="text-xs text-n-slate-10" data-testid="form-landing">
              <template v-if="form.pipeline_id">
                {{
                  $t('CARDS.FORMS.OPENS_CARD', {
                    pipeline: form.pipeline_name,
                    stage: form.stage_name,
                    type: form.task_type_name,
                  })
                }}
              </template>
              <template v-else>{{ $t('CARDS.FORMS.NO_CARD') }}</template>
            </span>
          </div>
          <Switch
            :model-value="form.status === 'active'"
            :aria-label="$t('CARDS.FORMS.TOGGLE', { name: form.name })"
            data-testid="form-toggle"
            @update:model-value="askToggle(form)"
          />
          <Button
            v-tooltip.top="$t('CARDS.FORMS.COPY_LINK')"
            :aria-label="$t('CARDS.FORMS.COPY_LINK')"
            icon="i-lucide-copy"
            size="sm"
            variant="ghost"
            color="slate"
            data-testid="form-copy-link"
            @click="copyLink(form)"
          />
          <Button
            :label="$t('CARDS.FORMS.EDIT')"
            icon="i-lucide-pencil"
            size="sm"
            variant="outline"
            color="slate"
            data-testid="form-edit"
            @click="editForm(form)"
          />
        </li>
      </ul>
    </template>
  </SettingsLayout>

  <Dialog
    ref="toggleDialog"
    type="alert"
    :title="toggleTexts.title"
    :description="toggleTexts.description"
    :confirm-button-label="toggleTexts.confirm"
    :is-loading="isToggling"
    @confirm="confirmToggle"
  />
</template>
